import { create } from 'zustand';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** A single action ("Undo") shown on the toast. */
  action?: { label: string; onClick: () => void };
  /** How long it stays, in ms. Errors stay longer. */
  durationMs?: number;
}

export interface ToastItem extends ToastInput {
  id: string;
  tone: ToastTone;
}

interface ToastState {
  items: readonly ToastItem[];
  /** True once any toast has been shown — the (lazy) host is only loaded from then on. */
  everShown: boolean;
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

let counter = 0;

export const useToastStore = create<ToastState>()((set) => ({
  items: [],
  everShown: false,
  push: (input) => {
    counter += 1;
    const id = `toast-${String(counter)}`;
    set((state) => ({
      items: [...state.items, { ...input, id, tone: input.tone ?? 'info' }].slice(-4),
      everShown: true,
    }));
    return id;
  },
  dismiss: (id) => {
    set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
  },
}));

/** Show a toast from anywhere (event handlers, mutation callbacks). Returns its id. */
export function toast(input: ToastInput): string {
  return useToastStore.getState().push(input);
}

export const toastSuccess = (title: string, description?: string): string =>
  toast({ title, tone: 'success', ...(description ? { description } : {}) });

export const toastError = (title: string, description?: string): string =>
  toast({ title, tone: 'error', ...(description ? { description } : {}) });
